import { motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import SummaryApi from '../common';
import displayNPRCurrency from '../helpers/displayCurrency';
import { request } from '../helpers/request';

const RefundHistory = () => {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRefunds = async () => {
        setLoading(true);
        try {
            const response = await request(SummaryApi.myRefunds);
            const resData = await response.json();
            if (resData.success) {
                setRefunds(resData.data);
            }
        } catch (err) {
            console.error("Error fetching refunds:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRefunds();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-600';
            case 'approved': return 'bg-blue-100 text-blue-600';
            case 'processed': return 'bg-green-100 text-green-600';
            case 'rejected': return 'bg-red-100 text-red-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <div className='bg-surface-muted min-h-screen pt-20 pb-32'>
            <div className='container mx-auto px-6 max-w-4xl'>
                <div className='mb-12'>
                    <h1 className='text-4xl font-black text-primary tracking-tight mb-2'>Return Requests</h1>
                    <p className='text-gray-500 font-medium'>Track the status of your returns and refunds.</p>
                </div>

                {loading ? (
                    <div className='space-y-6 animate-pulse'>
                        {[1, 2, 3].map(i => (
                            <div key={i} className='bg-white h-40 rounded-3xl border border-surface-border' />
                        ))}
                    </div>
                ) : refunds.length === 0 ? (
                    <div className='bg-white rounded-card p-16 text-center shadow-premium'>
                        <p className='text-gray-400 font-bold'>No return requests found.</p>
                    </div>
                ) : (
                    <div className='space-y-6'>
                        {refunds.map((refund, index) => (
                            <motion.div 
                                key={refund._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className='bg-white rounded-[2rem] p-6 md:p-8 border border-surface-border shadow-sm hover:shadow-premium transition-all'
                            >
                                <div className='flex flex-col md:flex-row md:items-center justify-between gap-6'>
                                    <div className='flex items-center gap-6'>
                                        <div className='w-20 h-20 bg-surface-muted rounded-2xl p-2 flex-shrink-0'>
                                            <img 
                                                src={refund.productId?.productImage?.[0]} 
                                                className='w-full h-full object-contain mix-blend-multiply' 
                                                alt={refund.productId?.productName} 
                                            />
                                        </div>
                                        <div>
                                            <h4 className='font-black text-primary mb-1'>{refund.productId?.productName}</h4>
                                            <p className='text-xs text-gray-400 font-bold uppercase tracking-widest'>{refund.reason}</p>
                                            <p className='text-[10px] text-gray-400 mt-2'>{moment(refund.createdAt).fromNow()}</p>
                                        </div>
                                    </div>

                                    <div className='flex flex-row md:flex-col items-center md:items-end justify-between gap-4'>
                                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${getStatusColor(refund.status)}`}>
                                            {refund.status}
                                        </div>
                                        {refund.refundAmount && (
                                            <p className='text-lg font-black text-accent'>{displayNPRCurrency(refund.refundAmount)}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {refund.adminComment && (
                                    <div className='mt-6 p-4 bg-surface-muted rounded-2xl'>
                                        <p className='text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1'>Message from Admin</p>
                                        <p className='text-sm font-medium text-primary'>{refund.adminComment}</p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RefundHistory;
